import LoadingAnimationIcon from '@/components/icons/loading-animation-icon';
import CardGiftProduct from '@/components/sections/builder/elements/header/header/cart-dropdown/card-gift-product';
import CardGiftProductItem from '@/components/sections/builder/elements/header/header/cart-dropdown/card-gift-product-item';
import { Button } from '@/components/ui/button';
import { DrawerClose, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useGetRewardProductInfinite,
  useGetRewardProductItemInfinite,
} from '@/hooks/api/storefront/promotion';
import { useDebounceValue } from '@/hooks/use-debounce';
import { ThemeColorSchemeType } from '@/stores/builder';
import { BaseFreeGift } from '@/stores/promotion';
import { HttpTypes } from '@/types';
import {
  BasePromotion,
  BasePromotionRewardProduct,
  BasePromotionRewardProductItem,
} from '@/types/http/promotion/common';
import { ArrowLeft, XIcon } from 'lucide-react';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

type FreeGiftListProps = {
  promotion: BasePromotion;
  token: string;
  drawerView: 'cart' | 'voucher' | 'gift' | 'active_offers';
  setDrawerView: React.Dispatch<
    React.SetStateAction<'cart' | 'voucher' | 'gift' | 'active_offers'>
  >;
  maxQty: number;
  onSaveGift: (data: BaseFreeGift[]) => void;
  colorScheme?: ThemeColorSchemeType;
  initialSelectedGifts?: BaseFreeGift[];
  wizardMode?: boolean;
  onNextStep?: () => void;
  locationId: string;
  capsByGroup?: Record<string, number>;
};

export default function FreeGiftList({
  drawerView,
  token,
  promotion,
  setDrawerView,
  maxQty,
  onSaveGift,
  colorScheme,
  initialSelectedGifts = [],
  wizardMode,
  onNextStep,
  locationId,
  capsByGroup,
}: FreeGiftListProps) {
  const { ref, inView } = useInView();
  const [selectedTab, setSelectedTab] = useState<'product_group' | 'product_item'>('product_group');
  const [selectedProducts, setSelectedProducts] = useState<BaseFreeGift[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<BasePromotionRewardProduct | undefined>();

  const [paramsProductGroup, setParamsProductGroup] = useState({
    page: 1,
    pageSize: 10,
    q: '',
  });
  const [paramsProductItem, setParamsProductItem] = useState({
    page: 1,
    pageSize: 10,
    q: '',
  });

  const debouncedSearchProduct = useDebounceValue(paramsProductGroup.q, 500);
  const debounceSearchItem = useDebounceValue(paramsProductItem.q, 500);

  const {
    data: dataRewardProduct,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
  } = useGetRewardProductInfinite(
    {
      promotion_id: promotion?.promotion_id as string,
      promotion_tier_id: promotion.promotion_tier_id as string,
      token,
      locationId,
    },
    {
      page: paramsProductGroup.page,
      pageSize: paramsProductGroup.pageSize,
      q: debouncedSearchProduct,
    },
    {
      enabled: !!promotion.promotion_id && drawerView === 'gift',
    }
  );

  const {
    data: dataRewardItem,
    fetchNextPage: fetchNextPageItem,
    hasNextPage: hasNextPageItem,
    isLoading: isLoadingItem,
    isFetchingNextPage: isFetchingNextPageItem,
  } = useGetRewardProductItemInfinite(
    {
      promotion_id: promotion?.promotion_id as string,
      product_id: selectedProduct?.product_group_id || '',
      promotion_tier_id: promotion.promotion_tier_id as string,
      token,
      locationId,
    },
    {
      page: paramsProductItem.page,
      pageSize: paramsProductItem.pageSize,
      q: debounceSearchItem,
    },
    {
      enabled: !!selectedProduct?.product_group_id && selectedTab === 'product_item',
    }
  );

  const normalize = (g: BaseFreeGift) => ({
    ...g,
    items: g.items.map((it) => ({ ...it, qty: Number(it.qty) || 0 })),
  });

  const hydratedKeyRef = useRef<string | null>(null);

  const listRewardProduct = useMemo(() => {
    return (
      (
        dataRewardProduct as unknown as HttpTypes.StorePromotionProductRewardListInfiniteScrollResponse
      )?.pages.flatMap((page) => page.data) || []
    );
  }, [dataRewardProduct]);
  const listRewardItem = useMemo(() => {
    return (
      (
        dataRewardItem as unknown as HttpTypes.StorePromotionProductRewardListItemInfiniteScrollResponse
      )?.pages.flatMap((page) => page.data) || []
    );
  }, [dataRewardItem]);

  const handleSearchProduct = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setParamsProductGroup((prev) => ({
      ...prev,
      q: e.target.value,
      page: 1,
    }));
  }, []);

  const handleSearchItem = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setParamsProductItem((prev) => ({
      ...prev,
      q: e.target.value,
      page: 1,
    }));
  }, []);

  const totalQty = useMemo(
    () =>
      selectedProducts.reduce(
        (sum, group) =>
          sum + group.items.reduce((gSum, item) => gSum + Number((item.qty ?? 0) || 0), 0),
        0
      ),
    [selectedProducts]
  );

  const handleUpdateQty = (
    productGroup: BasePromotionRewardProduct,
    item: BasePromotionRewardProductItem,
    delta: number
  ) => {
    const groupId = productGroup.product_group_id;
    const allowedAdd = delta > 0 ? Math.min(globalRemaining, groupRemaining(groupId)) : Infinity;
    const appliedDelta = delta > 0 ? Math.min(delta, allowedAdd) : delta;

    if (appliedDelta === 0) return;

    setSelectedProducts((prev) => {
      let touchedGroup = false;

      const next = prev
        .map((group) => {
          if (group.product_group.product_group_id !== groupId) return group;
          touchedGroup = true;

          const items = group.items.map((it) => ({ ...it, qty: Number(it.qty) || 0 }));
          const idx = items.findIndex((i) => i.item_id === item.item_id);

          if (idx !== -1) {
            const curr = Number(items[idx].qty) || 0;
            const newQty = curr + appliedDelta;
            if (newQty <= 0) {
              items.splice(idx, 1);
            } else {
              items[idx] = { ...items[idx], qty: newQty };
            }
          } else if (appliedDelta > 0) {
            items.push({ ...item, qty: appliedDelta });
          }

          return items.length > 0 ? { ...group, items } : null;
        })
        .filter((g): g is BaseFreeGift => g !== null);

      if (!touchedGroup && appliedDelta > 0) {
        next.push({
          product_group: productGroup,
          items: [{ ...item, qty: appliedDelta }],
        });
      }

      return next;
    });
  };

  const groupCap = (gid: string) => capsByGroup?.[gid] ?? maxQty;
  const pickedByGroup = useMemo(() => {
    const m = new Map<string, number>();
    selectedProducts.forEach((g) => {
      const gid = g.product_group.product_group_id;
      const sum = g.items.reduce((s, it) => s + (Number(it.qty) || 0), 0);
      m.set(gid, (m.get(gid) || 0) + sum);
    });
    return m;
  }, [selectedProducts]);

  const globalRemaining = Math.max(maxQty - totalQty, 0);
  const groupRemaining = (gid: string) =>
    Math.max(groupCap(gid) - (pickedByGroup.get(gid) || 0), 0);

  useEffect(() => {
    if (hasNextPage && inView) {
      fetchNextPage();
    }
  }, [inView, hasNextPage]);

  useEffect(() => {
    if (hasNextPageItem && inView) {
      fetchNextPageItem();
    }
  }, [inView, hasNextPageItem]);

  useEffect(() => {
    if (drawerView !== 'gift') {
      setSelectedProducts([]);
      setParamsProductGroup({ page: 1, pageSize: 10, q: '' });
    }
  }, [drawerView]);

  useEffect(() => {
    if (selectedTab !== 'product_group') {
      setParamsProductItem({ page: 1, pageSize: 10, q: '' });
    }
  }, [selectedTab]);

  useEffect(() => {
    if (drawerView !== 'gift') return;

    const key = `${promotion.promotion_id}:${promotion.promotion_tier_id ?? ''}`;

    if (hydratedKeyRef.current !== key) {
      setSelectedProducts((initialSelectedGifts || []).map(normalize));
      hydratedKeyRef.current = key;
    }
  }, [drawerView, promotion.promotion_id, promotion.promotion_tier_id, initialSelectedGifts]);

  return (
    <>
      <DrawerHeader className='flex items-center justify-between text-left'>
        <div className='flex items-center'>
          <Button
            variant={null}
            onClick={() => {
              if (drawerView === 'gift') {
                const isProductItemTab = selectedTab === 'product_item';
                if (isProductItemTab) {
                  setSelectedTab('product_group');
                } else {
                  setDrawerView('cart');
                }
              } else {
                setDrawerView('gift');
              }
            }}
            size='icon'
          >
            <ArrowLeft className='theme_cart_drawer_color h-[18px] w-[18px]' />
          </Button>
          <DrawerTitle className='theme_cart_drawer_color'>
            Choose your free gift ({totalQty}/{maxQty})
          </DrawerTitle>
        </div>
        <DrawerClose onClick={() => setDrawerView('cart')}>
          <XIcon className='h-4 w-4' />
        </DrawerClose>
      </DrawerHeader>

      {selectedTab === 'product_group' ? (
        <>
          <div className='px-4'>
            <div className='flex items-center gap-2'>
              <input
                type='text'
                placeholder='Search product...'
                value={paramsProductGroup.q}
                onChange={handleSearchProduct}
                className='w-full flex-1 rounded border px-3 py-2.5 text-xs outline-none ring-gray-200 focus:ring-1 focus:ring-[var(--ring-active-color)]'
                style={
                  {
                    '--ring-active-color': colorScheme?.background || '#000000',
                  } as React.CSSProperties
                }
              />
            </div>
          </div>

          <ScrollArea className='h-full overflow-y-auto px-4 pt-4' type='scroll'>
            <div className='flex flex-col gap-2'>
              {isLoading ? (
                <div className='space-y-1 overflow-y-auto'>
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <Skeleton className='h-10 w-full' key={idx} />
                  ))}
                </div>
              ) : listRewardProduct && listRewardProduct?.length > 0 ? (
                <>
                  {listRewardProduct?.map((product, index) => (
                    <CardGiftProduct
                      key={index}
                      product={product}
                      selectedProducts={selectedProducts}
                      setSelectedTab={setSelectedTab}
                      setSelectedProduct={setSelectedProduct}
                      colorScheme={colorScheme}
                    />
                  ))}
                  <div ref={hasNextPage ? ref : null}></div>
                  {hasNextPage && isFetchingNextPage && (
                    <div className='flex w-full justify-center py-4'>
                      <div className='flex'>
                        <LoadingAnimationIcon size={4} color={colorScheme?.background} />
                        <span className='-ml-1 text-xs'>Loading more</span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className='flex h-full items-center justify-center'>
                  <p className='text-sm text-gray-500'>No product found.</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </>
      ) : (
        <>
          <div className='px-4'>
            <div className='flex items-center gap-2'>
              <input
                type='text'
                placeholder='Search variant...'
                value={paramsProductItem.q}
                onChange={handleSearchItem}
                className='w-full flex-1 rounded border px-3 py-2.5 text-xs outline-none ring-gray-200 focus:ring-1 focus:ring-[var(--ring-active-color)]'
                style={
                  {
                    '--ring-active-color': colorScheme?.background || '#000000',
                  } as React.CSSProperties
                }
              />
            </div>
          </div>

          <ScrollArea className='h-full overflow-y-auto px-4 pt-4' type='scroll'>
            <div className='flex flex-col gap-2'>
              {isLoadingItem ? (
                <div className='space-y-1 overflow-y-auto'>
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <Skeleton className='h-10 w-full' key={idx} />
                  ))}
                </div>
              ) : listRewardItem && listRewardItem?.length > 0 ? (
                <>
                  {listRewardItem?.map((product) => (
                    <CardGiftProductItem
                      key={product.item_id}
                      product={product}
                      maxQty={maxQty}
                      selectedProduct={selectedProduct as BasePromotionRewardProduct}
                      selectedProducts={selectedProducts}
                      setSelectedTab={setSelectedTab}
                      handleUpdateQty={handleUpdateQty}
                      totalQty={totalQty}
                      colorScheme={colorScheme}
                    />
                  ))}
                  <div ref={hasNextPageItem ? ref : null}></div>
                  {hasNextPageItem && isFetchingNextPageItem && (
                    <div className='flex w-full justify-center py-4'>
                      <div className='flex'>
                        <LoadingAnimationIcon size={4} color={colorScheme?.background} />
                        <span className='-ml-1 text-xs'>Loading more</span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className='flex h-full items-center justify-center'>
                  <p className='text-sm text-gray-500'>No product found.</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </>
      )}

      <DrawerFooter className='px-4'>
        <div className='flex flex-col gap-2'>
          <Button
            variant='secondary'
            onClick={() => {
              onSaveGift([]);
              if (wizardMode && onNextStep) {
                onNextStep();
                setSelectedTab('product_group');
              } else {
                setDrawerView('cart');
              }
            }}
            className='theme_button theme_cart_drawer_btn w-full items-center px-24 underline-offset-2'
          >
            Skip
          </Button>

          <Button
            variant='secondary'
            onClick={() => {
              const selected: BaseFreeGift[] = selectedProducts
                .map((group) => {
                  const items = group.items
                    .filter((it) => (it.qty ?? 0) > 0)
                    .map((it) => ({ ...it, qty: it.qty! }));

                  return items.length > 0
                    ? ({
                        promotion_id: String(promotion.promotion_id),
                        product_group: group.product_group,
                        items,
                      } as BaseFreeGift)
                    : undefined;
                })
                .filter((g): g is BaseFreeGift => g !== undefined);

              onSaveGift(selected);

              if (wizardMode && onNextStep) {
                onNextStep();
                setSelectedTab('product_group');
              } else {
                setDrawerView('cart');
              }
            }}
            className='theme_button theme_cart_drawer_btn w-full items-center px-24 underline-offset-2'
            disabled={totalQty === 0}
          >
            {wizardMode ? 'Save & Next' : 'Add to cart'}
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
}
